import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTierMaster, getDefaultTierByMallId } from 'app/api/tier'; // getTierMaster: global tier-master read — see 02-RESEARCH.md Pitfall 3 (no mall-scoped tier fetch on this form). getDefaultTierByMallId: per-entry mall-scoped tier fetch for targetingEntries ONLY (03-RESEARCH.md Pitfall 2) — never mix with the global tierList below.
import { getMallList } from 'app/api/mall';
import { getUnassignedVoucherPool } from 'app/api/voucher';
import { uplaodMedia } from 'app/api/store';
import { getCampaignDetail, createCampaign, updateCampaign } from 'app/api/campaign';
import {
  buildCampaignFormData,
  parseCampaignSubmitError,
  getUncoveredTargets,
  hasDuplicateTargetingPairs,
  hasPointsConfigured,
  confirmVoucherClear,
  ACCRUAL_TIER_ID,
  ACCRUAL_UI_VALUE,
} from './submitHelpers';
import {
  validate,
  campaignValidationRules,
} from 'app/constants/validation-constants';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { dataformatChangesYearToDate, todayIstDate } from 'app/utils/Parsers';

// The Accrual sentinel is a legitimate tier value of zero (tierId === 0) —
// truthiness tests on a tier id (`!entry.tierId`, `entry.tierId &&`) are the
// bug class this helper exists to prevent (D-08/D-13). Empty string also
// counts as unset: mall/tier dropdowns represent "nothing selected" as ''
// (not null/undefined), so a bare `== null` check would wrongly accept an
// untouched row as complete.
function isUnset(value) {
  return value === null || value === undefined || value === '';
}

// D-15: Accrual is not a real tier row and must never be injected server-side
// into the shared `getDefaultTierByMallId` endpoint — that endpoint has five
// consumers (including Festive Tier creation and Notification targeting)
// where an Accrual option is meaningless. This option is therefore appended
// client-side only, at every site that builds a per-mall tier option list.
// Shaped to match the server's own mapped items (`value`/`id`/`tierId` all
// set to ACCRUAL_TIER_ID) so every downstream lookup that compares
// `String(option.value)` against a stored tierId keeps working unchanged.
// The literal zero value is safe here because every consumer of this list
// (conceptTierIdOfEntry, addTargetForTier, toggleMallTier) compares through
// String() — unlike the Slab From-tier field elsewhere in this file, which
// uses bare truthy checks and therefore needs a separate truthy UI proxy
// value (see submitHelpers.js's exported Slab-Upgrade constant). Do not
// reuse that proxy here — a second competing proxy would itself become a
// new footgun.
const ACCRUAL_TARGET_OPTION = {
  value: ACCRUAL_TIER_ID,
  id: ACCRUAL_TIER_ID,
  tierId: ACCRUAL_TIER_ID,
  label: 'Accrual',
};

// Appends ACCRUAL_TARGET_OPTION to a (possibly undefined) per-mall tier
// option list, skipping the append if an option with that value is already
// present. Every mallTierOptions build site routes through this single
// helper — never a local copy — so the fan-out load and the edit-mode
// hydration path can never drift apart.
function withAccrualOption(serverOptions) {
  const options = serverOptions || [];
  const alreadyPresent = options.some(
    (option) => String(option.value) === String(ACCRUAL_TIER_ID)
  );
  return alreadyPresent ? options : [...options, ACCRUAL_TARGET_OPTION];
}

const useCreateCampaign = () => {
  const navigate = useNavigate();

  // CAMP-07 edit-mode bridge (RESEARCH Pattern 5) — same LocalStorage
  // convention as useCampaignDetail.js, read synchronously (no useParams
  // anywhere in this codebase). `campaignData === null` means no bridge
  // object exists at all (the normal, unblocked create-mode path — e.g. the
  // "Create Campaign" button, which clears this key before navigating, see
  // useGamificationCampaigns.js). `campaignData` present but missing a valid
  // `id` means a malformed/corrupted edit bridge (RESEARCH Pitfall 7) — that
  // case is guarded below, never a plain empty bridge.
  const campaignData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CAMPAIGN_DATA) || 'null'
  );
  const campaignId = campaignData?.id ?? null;

  const [tierList, setTierList] = useState([]);
  // Mall dropdown options for the Targeting section's per-entry Mall
  // dropdown (RWD-01) — mirrors useCreateVoucher.js's getMallListforDropdown.
  const [mallList, setMallList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setErrors] = useState({});
  const [values, setValues] = useState({
    name: '',
    gameType: '',
    triggerType: '',
    // Default to 'ALL' rather than a blank placeholder: an unset gender means
    // "all genders" anyway, so pre-selecting All matches the intended default
    // and is a valid backend enum value (TARGET_GENDERS).
    targetGender: 'ALL',
    startDate: '',
    endDate: '',
    // A new campaign defaults to published, matching the backend column
    // default — this is the admin's draft/live toggle, flippable at any
    // point in a campaign's life (Phase 9, D-09).
    publish: true,
    // Removes ONLY the random draw in decideWin; every configured limit
    // (schedule, daily wins-per-day, remaining stock) still applies
    // (Phase 9, D-05/D-06 — replaces the retired 0-100 win-odds seed field).
    alwaysWin: false,
    // Bill window — which invoice dates count, independent of the campaign
    // window (startDate/endDate) above (Phase 9).
    billStartDate: '',
    billEndDate: '',
    // Bonus points (optional).
    totalPointsBudget: '',
    pointsPerWin: '',
    singleDaySpendMin: '',
    singleDaySpendMax: '',
    birthdayWindowDays: '',
    bannerImage: '',
    backgroundImage: '',
  });
  // Kept as its own array state (not nested inside `values`), mirroring how
  // CreateMenu keeps imageUploadArray separate from its scalar form fields.
  const [slabUpgradePaths, setSlabUpgradePaths] = useState([]);

  // Cascaded (mall, tier) targeting builder (RWD-01/02/03, D-01..D-04).
  // Row shape: { addid, mallId, tierId, mallName, tierName,
  // minSpendThreshold, winsPerDay, voucherIds, voucherPool,
  // assignedVouchers }. winsPerDay rides beside minSpendThreshold — one
  // extra column on the structure that already renders one row per
  // targeted (mall, tier) pair (Phase 9 SC-17). It shares the same bulk
  // toolbar as minSpendThreshold (applyBulkDefaults, below) — each field is
  // only applied when its own bulk box has a value, so setting one without
  // the other never touches per-row values for the field left blank.
  // `voucherPool` is per-entry — never shared with the global `tierList`
  // below, which stays reserved for Slab Upgrade (03-RESEARCH.md Pitfall 2).
  // This is the CANONICAL flat source of truth that submitHelpers.js reads
  // (buildCampaignFormData/getUncoveredTargets/hasDuplicateTargetingPairs) —
  // one item per selected (mall, tier) target. The mall-grouped accordion
  // rendered in index.js (`groupedMalls` below) is a pure DERIVATION of this
  // array, never a second persisted structure (quick task 260720-ntx).
  const [targetingEntries, setTargetingEntries] = useState([]);

  // Quick task 260720-ntx (Targeting redesign): per-mall tier-options cache
  // — { [mallId]: tierOptions[] } — so the mall-grouped accordion can render
  // every mall's tier pills BEFORE any (mall,tier) target exists (RWD-01/02).
  // Populated once mallList is known (see the fan-out effect below) and
  // seeded per-mall by hydrateTargetingEntryOptions in edit mode.
  const [mallTierOptions, setMallTierOptions] = useState({});
  // Which malls have already had their tier options fetched — guards the
  // fan-out effect against re-fetching on every unrelated mallList
  // reference change.
  const fetchedMallTierIds = useRef(new Set());

  // Shared bulk Min Spend / Wins Per Day values (bulk toolbar) and which
  // mall rows are expanded (accordion disclose state).
  const [bulkMinSpend, setBulkMinSpend] = useState('');
  const [bulkWinsPerDay, setBulkWinsPerDay] = useState('');
  const [openMalls, setOpenMalls] = useState(() => new Set());

  function toggleMallOpen(mallId) {
    setOpenMalls((prev) => {
      const next = new Set(prev);
      if (next.has(mallId)) {
        next.delete(mallId);
      } else {
        next.add(mallId);
      }
      return next;
    });
  }

  // CAMP-07 edit-mode: the raw campaign detail response (source of the
  // server-derived `status` the structural lock reuses — RESEARCH Pitfall
  // 6) and the voucherUpdates[] quantity-correction draft (D-10).
  const [detail, setDetail] = useState(null);
  const [voucherUpdates, setVoucherUpdates] = useState([]);

  // Baseline of voucher ids assigned to THIS campaign at edit-load time (from
  // detail.vouchers). submitCampaign diffs it against the final voucherIds to
  // compute releaseVoucherIds so removed vouchers are freed, never orphaned
  // (quick task 260729-gws). Empty in create mode.
  const [originalVoucherIds, setOriginalVoucherIds] = useState([]);
  const [voucherUpdateErrors, setVoucherUpdateErrors] = useState({});

  // Backend field-level errors (D-06/D-08) keyed so index.js can map each
  // error inline to a specific targeting-entry card or voucher row. Keyed
  // by targetingEntries[idx].addid for tierConfig[idx].* errors, or
  // `${addid}-voucher-${voucherId}` for voucherIds[idx] errors. Cleared at
  // the start of every new submit attempt.
  const [fieldErrors, setFieldErrors] = useState({});

  async function getTierListDropdown() {
    setPageLoading(true);
    let response = await getTierMaster(); // global tier list — takes no scoping argument
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = response?.data?.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      }));
      setTierList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }

  async function getMallListforDropdown() {
    setPageLoading(true);
    const response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      const temp = response?.data?.malls?.map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      }));
      setMallList(temp);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }

  useEffect(() => {
    getTierListDropdown();
    getMallListforDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quick task 260720-ntx: fan out getDefaultTierByMallId(mall.id) across
  // every mall in mallList (Promise.all) to populate mallTierOptions — this
  // is what lets tier pills render for every mall before any target exists
  // (RWD-01/02). Guarded by fetchedMallTierIds so a mallList reference
  // change (e.g. after edit-mode hydration also touches mallList indirectly)
  // never re-fetches malls already cached.
  useEffect(() => {
    if (!mallList || mallList.length === 0) return undefined;
    const toFetch = mallList.filter(
      (mall) => !fetchedMallTierIds.current.has(mall.id)
    );
    if (toFetch.length === 0) return undefined;
    toFetch.forEach((mall) => fetchedMallTierIds.current.add(mall.id));

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        toFetch.map((mall) => getDefaultTierByMallId(mall.id))
      );
      if (cancelled) return;
      setMallTierOptions((prev) => {
        const next = { ...prev };
        toFetch.forEach((mall, idx) => {
          const response = results[idx];
          next[mall.id] =
            response && response?.responseCode === 200
              ? withAccrualOption(
                  response?.data?.map((item) => ({
                    ...item,
                    value: item.id,
                    label: item.name,
                  })) || []
                )
              // A mall whose tier fetch failed still offers Accrual rather
              // than an empty list — wrap the fallback branch too, so both
              // paths through this ternary go through the shared helper.
              : withAccrualOption(next[mall.id] || []);
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mallList]);

  // Per-entry mall-scoped tier options + (mall,tier)-scoped unassigned
  // voucher pool for a HYDRATED targeting entry — same fetch shape
  // addTargetForTier makes for a freshly-toggled pill, but adapted for
  // prefill: never resets tierId/voucherIds/assignedVouchers (those already
  // carry the correct hydrated values), only fills in the option lists so
  // pre-start (SCHEDULED) free-editing (D-11) has a pool to add new vouchers
  // from.
  async function hydrateTargetingEntryOptions(addid, mallId, tierId) {
    // D-08 item 2: `tierId != null`, not raw truthiness — tierId 0 is the
    // Accrual sentinel and is falsy in JS. A truthiness gate here silently
    // skipped the voucher-pool fetch for an Accrual target, leaving its pill
    // showing an empty pool.
    const [tierRes, poolRes] = await Promise.all([
      getDefaultTierByMallId(mallId),
      tierId != null
        ? getUnassignedVoucherPool({ mallId, tierId, campaignId })
        : Promise.resolve(null),
    ]);
    const patch = {};
    if (tierRes && tierRes?.responseCode === 200) {
      // D-15: wrap the mapped result once and use the SAME wrapped value for
      // both patch.tierOptions and setMallTierOptions below — otherwise the
      // per-entry tierOptions and the mall-grouped accordion's mallTierOptions
      // could drift, one offering Accrual and the other not.
      const tierOptions = withAccrualOption(
        tierRes?.data?.map((item) => ({
          ...item,
          value: item.id,
          label: item.name,
        }))
      );
      patch.tierOptions = tierOptions;
      // Quick task 260720-ntx: seed mallTierOptions for this hydrated mall
      // too, so its tier pills render (the mallList fan-out effect above
      // only fires for the admin's assigned mall scope — this covers a
      // hydrated edit-mode mall the same way).
      fetchedMallTierIds.current.add(mallId);
      setMallTierOptions((prev) => ({ ...prev, [mallId]: tierOptions }));
    }
    if (poolRes && poolRes?.responseCode === 200) {
      patch.voucherPool = poolRes?.data?.data || [];
    }
    updateTargetingEntry(addid, patch);
  }

  // CAMP-07 edit-mode prefill (RESEARCH Pattern 5 / Pitfall 6/7), adapted
  // from useCampaignDetail.js's guarded async hydrate-on-mount.
  useEffect(() => {
    // No bridge object at all — normal create-mode, nothing to hydrate.
    if (campaignData === null) {
      return undefined;
    }
    // Bridge object exists but carries no valid id — a malformed/corrupted
    // edit bridge (RESEARCH Pitfall 7). Guard rather than render an
    // editable-but-empty form that could submit garbage.
    if (!campaignId) {
      Toast.error(Message?.Error?.COMMON_MESSAGE);
      navigate(APP_CONSTANTS.GAMIFICATION_CAMPAIGNS);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setPageLoading(true);
      const response = await getCampaignDetail(campaignId);
      if (cancelled) return;

      if (response?.responseCode === 200) {
        const data = response.data;
        setDetail(data);
        setValues((prev) => ({
          ...prev,
          name: data.name ?? '',
          gameType: data.gameType ?? '',
          triggerType: data.triggerType ?? '',
          // A legacy campaign with no stored gender (null) reads as "all
          // genders" — fall back to 'ALL' so edit mode shows All selected
          // rather than a blank placeholder (matches the create-mode default).
          targetGender: data.targetGender ?? 'ALL',
          startDate: data.startDate
            ? dataformatChangesYearToDate(data.startDate)
            : '',
          endDate: data.endDate
            ? dataformatChangesYearToDate(data.endDate)
            : '',
          publish: data.publish ?? true,
          alwaysWin: data.alwaysWin ?? false,
          billStartDate: data.billStartDate
            ? dataformatChangesYearToDate(data.billStartDate)
            : '',
          billEndDate: data.billEndDate
            ? dataformatChangesYearToDate(data.billEndDate)
            : '',
          totalPointsBudget: data.totalPointsBudget ?? '',
          pointsPerWin: data.pointsPerWin ?? '',
          singleDaySpendMin: data.singleDaySpendMin ?? '',
          singleDaySpendMax: data.singleDaySpendMax ?? '',
          birthdayWindowDays: data.birthdayWindowDays ?? '',
          bannerImage: data.bannerImage ?? '',
          backgroundImage: data.backgroundImage ?? '',
        }));
        setSlabUpgradePaths(
          (data.slabUpgradePaths || []).map((row) => ({
            addid: `slab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            // Backend sentinel 0 (from Accrual) -> the truthy UI proxy -1 so
            // the From Dropdown renders "Accrual" instead of blank (260730-m22).
            fromTierId:
              Number(row.fromTierId) === ACCRUAL_TIER_ID
                ? ACCRUAL_UI_VALUE
                : row.fromTierId,
            toTierId: row.toTierId,
          }))
        );

        // Rebuild one targeting entry per tierConfig pair, carrying each
        // pair's already-assigned vouchers (with their voucherCount/remaining
        // fields untouched — the floor-check/removability baseline per
        // RESEARCH Pitfall 10, never recomputed).
        const rebuiltEntries = (data.tierConfig || []).map((tc) => ({
          addid: `target-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          mallId: tc.mallId,
          mallName: tc.mallName ?? null,
          tierId: tc.tierId,
          tierName: tc.tierName ?? null,
          tierOptions: [],
          minSpendThreshold: tc.minSpendThreshold ?? '',
          winsPerDay: tc.winsPerDay ?? '',
          voucherIds: (data.vouchers || [])
            .filter(
              (v) =>
                v.mallId === tc.mallId &&
                (v.tierIds?.length ? v.tierIds.includes(tc.tierId) : v.tierId === tc.tierId)
            )
            .map((v) => v.id),
          voucherPool: [],
          assignedVouchers: (data.vouchers || []).filter(
            (v) =>
              v.mallId === tc.mallId &&
              (v.tierIds?.length ? v.tierIds.includes(tc.tierId) : v.tierId === tc.tierId)
          ),
        }));
        setTargetingEntries(rebuiltEntries);

        // Snapshot every voucher currently assigned to this campaign as the
        // release-diff baseline (quick task 260729-gws).
        setOriginalVoucherIds((data.vouchers || []).map((v) => v.id));

        rebuiltEntries.forEach((entry) => {
          if (entry.mallId) {
            hydrateTargetingEntryOptions(entry.addid, entry.mallId, entry.tierId);
          }
        });
      } else {
        Toast.error(
          response?.response?.message || Message?.Error?.COMMON_MESSAGE
        );
      }
      setPageLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin override (product decision): the post-start structural lock has been
  // removed — every field is editable at any lifecycle stage, including after
  // the campaign has started. `isLocked` is kept (referenced across the form)
  // but pinned false so nothing disables. The backend matches this (the
  // updateCampaign guards were removed).
  const isLocked = false;

  // Client feedback (Sep 2026): opening an ENDED campaign for edit showed its
  // Publish toggle ON, which reads as "this is live" for a campaign members
  // can no longer see — the confusion this flag exists to remove.
  //
  // Derived from the end date CURRENTLY IN THE FORM, never from
  // detail.status: an admin who extends an ended campaign's end date must get
  // the toggle back in the same session, otherwise they could never republish
  // the campaign they just extended. Compared as plain 'YYYY-MM-DD' strings
  // (the shape both the date inputs and the API use) against the IST calendar
  // date, mirroring the server's own ENDED rule exactly
  // (deriveCampaignStatus: `today > campaign.endDate`) — STRICTLY after, so a
  // campaign is still live on its end date itself, and IST rather than the
  // UTC-based commonMinDate() so the form and the listing cannot disagree
  // between midnight and 05:30.
  //
  // Deliberately NOT gated on triggerType. The rule is "does this campaign
  // carry an end date that has passed". Only DATE_BASED exposes the date
  // inputs, so in practice only those campaigns reach it — but a legacy
  // campaign on another trigger that already holds a past window shows Ended
  // in the listing too, and must read the same way here.
  const isPastEndDate =
    Boolean(values.endDate) && String(values.endDate) < todayIstDate();

  // What the Publish toggle DISPLAYS past the end date, tracked separately
  // from `values.publish` so the stored value is never written there
  // (decision, Sep 2026: presentation only). Every edit save sends the full
  // structural payload including `publish`, so forcing the stored value to
  // false would silently unpublish a campaign whenever an admin saved an
  // unrelated fix on it — and would turn a later end-date extension into a
  // draft instead of putting the campaign back the way it is today.
  //
  // Starts false: an ended campaign opens with Publish showing off, which is
  // the whole point. The admin can still switch it on — the toggle moves
  // normally; it is SAVE that refuses (see handleCreateCampaign), so the
  // block is stated once, in the place the admin is actually acting.
  const [publishAttempt, setPublishAttempt] = useState(false);

  // What the Publish toggle SHOWS. Past the end date that is the admin's own
  // switch position; otherwise it is simply the stored value.
  const publishDisplayValue = isPastEndDate ? publishAttempt : values.publish;

  // Save is blocked while an ended campaign is switched to Publish — the one
  // combination that cannot be persisted. Everything else about an ended
  // campaign (voucher counts, images, targeting) stays editable.
  const publishBlockedByEndDate = isPastEndDate && publishAttempt;

  // Carries the admin's intent across an end-date extension. Turning Publish
  // on and THEN extending the end date is the natural way to relaunch a
  // campaign; without this the switch would silently snap back to whatever
  // was stored the moment the campaign stopped being ended.
  useEffect(() => {
    if (!isPastEndDate && publishAttempt) {
      setPublishAttempt(false);
      setChange('publish', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPastEndDate, publishAttempt]);

  // CAMP-07/D-12 voucher-release carve-out: while pre-start (SCHEDULED),
  // DEACTIVATED, or a running (ACTIVE) campaign, an assigned voucher can
  // still be removed (releasing it back to the pool on submit) even though
  // the bulk structural fields are otherwise locked while DEACTIVATED. The
  // backend permits release at any lifecycle stage under a row lock (quick
  // task 260729-gws).
  //
  // Quick task 260820-gyo (D-5): detaching a voucher from a campaign is now
  // ALWAYS allowed, including after units have been issued from it — the
  // backend's zero-issuance rejection was removed entirely. isVoucherRemovable
  // no longer gates on issued === 0; it only mirrors the lifecycle-status
  // gate above so the client never silently withholds a decision the backend
  // will honor.
  const canReleaseVouchers =
    detail?.status === 'SCHEDULED' ||
    detail?.status === 'DEACTIVATED' ||
    detail?.status === 'ACTIVE';

  function isVoucherRemovable(voucher) {
    if (!canReleaseVouchers || !voucher) return false;
    return true;
  }

  function findAssignedVoucher(voucherId) {
    for (const entry of targetingEntries) {
      const found = (entry.assignedVouchers || []).find(
        (v) => v.id === voucherId
      );
      if (found) return found;
    }
    return null;
  }

  // Removes an assigned voucher from its targeting entry (CAMP-07/D-12) —
  // no-ops only on lifecycle status (ENDED, or a status outside
  // canReleaseVouchers). Quick task 260820-gyo (D-5): detach no longer
  // depends on issuance — the backend always allows it, including for a
  // voucher units have already been issued from. The dropped id is
  // reflected in the entry's voucherIds so a DEACTIVATED submit sends the
  // reduced set (T-03-10).
  function handleRemoveAssignedVoucher(entryIdx, voucherId) {
    setTargetingEntries((prev) =>
      prev.map((row, idx) => {
        if (idx !== entryIdx) return row;
        const voucher = (row.assignedVouchers || []).find(
          (v) => v.id === voucherId
        );
        if (!isVoucherRemovable(voucher)) {
          return row;
        }
        return {
          ...row,
          assignedVouchers: (row.assignedVouchers || []).filter(
            (v) => v.id !== voucherId
          ),
          voucherIds: (row.voucherIds || []).filter((id) => id !== voucherId),
        };
      })
    );
  }

  // D-10: voucherUpdates[] quantity-correction, floor-checked client-side
  // against the issued count baseline from the detail response (RESEARCH
  // Pitfall 10 — never recomputed independently).
  function handleVoucherUpdateChange(voucherId, count) {
    const voucher = findAssignedVoucher(voucherId);
    const issued = (voucher?.voucherCount ?? 0) - (voucher?.remaining ?? 0);
    const numericCount = Number(count);

    if (Number.isNaN(numericCount) || numericCount < issued) {
      setVoucherUpdateErrors((prev) => ({
        ...prev,
        [voucherId]: `Cannot reduce below ${issued} — that many have already been issued.`,
      }));
      return;
    }

    setVoucherUpdateErrors((prev) => ({ ...prev, [voucherId]: '' }));
    setVoucherUpdates((prev) => {
      const idx = prev.findIndex((u) => u.voucherId === voucherId);
      const next = { voucherId, voucherCount: numericCount };
      if (idx === -1) return [...prev, next];
      const copy = [...prev];
      copy[idx] = next;
      return copy;
    });
  }

  function handleChange(e) {
    if (e && e.target) {
      const { name, value } = e.target;

      // Switching triggers must clear the previously-selected trigger's
      // parameters at the source (CR-02) — otherwise a stale
      // singleDaySpendMin/Max or birthdayWindowDays value can survive the
      // switch and feed a validation rule that belongs to a different
      // trigger, silently and permanently blocking Save.
      if (name === 'triggerType') {
        setValues((prevState) => ({
          ...prevState,
          triggerType: value,
          singleDaySpendMin: '',
          singleDaySpendMax: '',
          birthdayWindowDays: '',
        }));
        setSlabUpgradePaths([]);

        if (error[name]) {
          setErrors({
            ...error,
            [name]: '',
          });
        }
        return;
      }

      setValues((prevState) => ({
        ...prevState,
        [name]: value,
      }));

      if (error[name]) {
        setErrors({
          ...error,
          [name]: '',
        });
      }
    }
  }

  function setChange(name, val) {
    setValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
    setErrors((prevError) => ({
      ...prevError,
      [name]: '',
    }));
  }

  async function uploadMediaHandler(file, name) {
    if (!file) {
      setChange(name, null);
      return;
    }
    // Asset upload is not gated behind campaign submit (D-06) — this call is real.
    const response = await uplaodMedia(file);
    if (response?.responseCode === 200) {
      Toast.success('Image upload Successfull');
      const data = await response.data;
      setChange(name, data?.url);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  }

  function addUpgradePath() {
    setSlabUpgradePaths((prev) => [
      ...prev,
      {
        // Date.now() alone can collide on rapid double-add within the same
        // millisecond — append a random suffix to guarantee uniqueness.
        addid: `slab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        fromTierId: null,
        toTierId: null,
      },
    ]);
  }

  function removeUpgradePath(addid) {
    const next = slabUpgradePaths.filter((row) => row.addid !== addid);
    setSlabUpgradePaths(next);
    // Option A: removing a rule may drop a tier out of the reward set — prune
    // any targeting entry that is no longer a valid To-Tier.
    pruneTargetsToRewardSet(next);
  }

  // Adjacent, reverse, and non-adjacent {fromTierId, toTierId} combinations
  // are all still accepted here — there is NO client-side adjacency/direction
  // guard (CAMP-03). Same-tier rows (fromTierId === toTierId within one row)
  // are now prevented: proactively via cross-filtered Dropdown options in
  // index.js, and enforced by a submit-time backstop in handleCreateCampaign
  // below. This amends CAMP-03 per quick task 260715-exi.
  function handleUpgradePathChange(addid, field, value) {
    const next = slabUpgradePaths.map((row) =>
      row.addid === addid ? { ...row, [field]: value } : row
    );
    setSlabUpgradePaths(next);
    // Option A: a changed From/To tier can drop a tier out of the reward set —
    // prune any targeting entry that is no longer a valid To-Tier (D-03 confirm
    // when it holds vouchers).
    pruneTargetsToRewardSet(next);
  }

  function removeTargetingEntry(addid) {
    setTargetingEntries((prev) => prev.filter((row) => row.addid !== addid));
  }

  // Thin field-level updater — the underlying primitive for
  // setEntryMinSpend/setEntryVouchers below. Not exported: index.js only
  // ever calls the named setters, keeping the "which field can index.js
  // touch" surface explicit.
  function handleTargetingEntryChange(addid, field, value) {
    setTargetingEntries((prev) =>
      prev.map((row) =>
        row.addid === addid ? { ...row, [field]: value } : row
      )
    );
  }

  function updateTargetingEntry(addid, patch) {
    setTargetingEntries((prev) =>
      prev.map((row) => (row.addid === addid ? { ...row, ...patch } : row))
    );
  }

  // Slab-Upgrade reward set: the distinct destination (To-Tier) CONCEPT ids
  // across COMPLETE upgrade rules (both fromTierId and toTierId set). A member
  // only ever lands in a rule's To-Tier, so ONLY these tiers may be targeted
  // when triggerType === 'SLAB_UPGRADE'. null when the trigger is not slab
  // (feature off). toTierId comes from tierList (getTierMaster) — already a
  // concept id; targeting pills carry their concept id on option.tierId (the
  // Default_Tiers.tierId FK), so membership is a concept-id compare, never the
  // per-mall option.value (Default_Tiers.id) used for storage everywhere else.
  const slabRewardTierIds = useMemo(() => {
    if (values.triggerType !== 'SLAB_UPGRADE') return null;
    const set = new Set();
    slabUpgradePaths.forEach((row) => {
      if (row.fromTierId && row.toTierId) set.add(String(row.toTierId));
    });
    return set;
  }, [values.triggerType, slabUpgradePaths]);

  // Resolve a targeting entry's tier to its CONCEPT id (tier_master id) via
  // mallTierOptions — entries store the per-mall Default_Tiers id in .tierId,
  // but slab reward-set membership is a concept-id question.
  const conceptTierIdOfEntry = (entry) => {
    const opt = (mallTierOptions[entry.mallId] || []).find(
      (o) => String(o.value) === String(entry.tierId)
    );
    return opt?.tierId;
  };

  // Option A re-sync (slab): when upgrade rules change, drop any targeting
  // entry whose tier is no longer a rule To-Tier — gated by the shared D-03
  // voucher-clear confirm when the orphaned entries hold vouchers. `nextPaths`
  // is passed explicitly because setSlabUpgradePaths hasn't flushed yet.
  function pruneTargetsToRewardSet(nextPaths) {
    const rewardSet = new Set();
    (nextPaths || []).forEach((row) => {
      if (row.fromTierId && row.toTierId) rewardSet.add(String(row.toTierId));
    });
    const orphaned = targetingEntries.filter((entry) => {
      const concept = conceptTierIdOfEntry(entry);
      // Unresolved concept (options not loaded yet) -> leave it; the submit
      // validation is the backstop.
      return concept !== undefined && !rewardSet.has(String(concept));
    });
    if (orphaned.length === 0) return;
    const orphanIds = new Set(orphaned.map((e) => e.addid));
    const voucherCount = orphaned.reduce(
      (sum, e) => sum + (e.voucherIds?.length || 0),
      0
    );
    const doRemove = () =>
      setTargetingEntries((prev) => prev.filter((e) => !orphanIds.has(e.addid)));
    if (voucherCount > 0) {
      confirmVoucherClear(voucherCount, doRemove);
    } else {
      doRemove();
    }
  }

  // Quick task 260720-ntx: creates the flat entry for a newly-selected
  // (mall, tier) pill and fetches its unassigned voucher pool (RWD-03) —
  // GAMIFICATION-base endpoint, never the normal VOUCHER list (RESEARCH
  // Pitfall 3). tierIds[]-aware pool matching is entirely server-side; this
  // client only forwards the array-form {mallId, tierId} the pool endpoint
  // already expects — never reintroduces a scalar tierId match locally.
  async function addTargetForTier(mallId, tierId) {
    const mall = mallList.find((m) => String(m.id) === String(mallId));
    const tierOption = (mallTierOptions[mallId] || []).find(
      (t) => String(t.value) === String(tierId)
    );
    const addid = `target-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setTargetingEntries((prev) => [
      ...prev,
      {
        addid,
        mallId,
        tierId,
        mallName: mall?.label ?? null,
        tierName: tierOption?.label ?? null,
        tierOptions: mallTierOptions[mallId] || [],
        // Default to the shared bulk value when one is set, per Task 1 step 4.
        minSpendThreshold: bulkMinSpend || '',
        // Mirrors minSpendThreshold: seed from the shared bulk Wins/Day
        // value when one is set (Phase 9 SC-17 bulk toolbar).
        winsPerDay: bulkWinsPerDay || '',
        voucherIds: [],
        voucherPool: [],
      },
    ]);
    setOpenMalls((prev) => new Set(prev).add(mallId));

    setPageLoading(true);
    const response = await getUnassignedVoucherPool({
      mallId,
      tierId,
      campaignId,
    });
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      updateTargetingEntry(addid, { voucherPool: response?.data?.data || [] });
    } else {
      // WR-02: don't leave the pool holding vouchers from a stale request —
      // nothing in the UI re-labels them.
      updateTargetingEntry(addid, { voucherPool: [] });
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message?.Error?.COMMON_MESSAGE
      );
    }
  }

  // Quick task 260720-ntx: tier-pill toggle — the single interaction that
  // replaces the old two-step Mall dropdown -> Tier dropdown flow. If the
  // (mall,tier) pair already has a flat entry, toggling removes it (gated by
  // D-03 confirmVoucherClear when that entry already holds vouchers);
  // otherwise it creates one and auto-expands the mall (RWD-02).
  function toggleMallTier(mallId, tierId) {
    // Slab Upgrade: only rule To-Tiers are selectable — defensively ignore a
    // toggle on a non-destination tier even if a disabled pill were clicked.
    if (slabRewardTierIds) {
      const opt = (mallTierOptions[mallId] || []).find(
        (o) => String(o.value) === String(tierId)
      );
      if (!opt || !slabRewardTierIds.has(String(opt.tierId))) return;
    }
    const entry = targetingEntries.find(
      (row) =>
        String(row.mallId) === String(mallId) &&
        String(row.tierId) === String(tierId)
    );
    if (entry) {
      if ((entry.voucherIds || []).length > 0) {
        confirmVoucherClear(entry.voucherIds.length, () =>
          removeTargetingEntry(entry.addid)
        );
        return;
      }
      removeTargetingEntry(entry.addid);
      return;
    }
    addTargetForTier(mallId, tierId);
  }

  // Quick task 260720-ntx: per-mall tri-state control. All tiers selected ->
  // remove every one of that mall's entries (D-03 gated on the combined
  // voucher count) and collapse the row; otherwise add an entry for every
  // not-yet-selected tier and expand it.
  function toggleMall(mallId) {
    // Slab Upgrade: the mall tri-state only ever operates on rule To-Tier
    // pills — disabled (non-destination) tiers are never selected/counted.
    const allTierOptions = mallTierOptions[mallId] || [];
    const tierOptions = slabRewardTierIds
      ? allTierOptions.filter((t) => slabRewardTierIds.has(String(t.tierId)))
      : allTierOptions;
    const mallEntries = targetingEntries.filter(
      (row) => String(row.mallId) === String(mallId)
    );
    const allSelected =
      tierOptions.length > 0 && mallEntries.length === tierOptions.length;

    if (allSelected) {
      const voucherCount = mallEntries.reduce(
        (sum, row) => sum + (row.voucherIds?.length || 0),
        0
      );
      const doRemove = () => {
        setTargetingEntries((prev) =>
          prev.filter((row) => String(row.mallId) !== String(mallId))
        );
        setOpenMalls((prev) => {
          const next = new Set(prev);
          next.delete(mallId);
          return next;
        });
      };
      if (voucherCount > 0) {
        confirmVoucherClear(voucherCount, doRemove);
      } else {
        doRemove();
      }
      return;
    }

    const selectedTierIds = new Set(mallEntries.map((row) => String(row.tierId)));
    tierOptions
      .filter((tier) => !selectedTierIds.has(String(tier.value)))
      .forEach((tier) => addTargetForTier(mallId, tier.value));
    setOpenMalls((prev) => new Set(prev).add(mallId));
  }

  // Quick task 260720-ntx: top tri-state "All malls" control. Every
  // (mall,tier) pair selected -> clear every entry (D-03 gated on the grand
  // total voucher count) and collapse all mall rows; otherwise add every
  // missing (mall,tier) entry and expand every mall.
  // Slab Upgrade: across every helper here, a mall's selectable tiers are only
  // its rule-To-Tier pills; non-destination tiers never enter the counts or the
  // add loop.
  const selectableTierOptions = (mallId) => {
    const opts = mallTierOptions[mallId] || [];
    return slabRewardTierIds
      ? opts.filter((t) => slabRewardTierIds.has(String(t.tierId)))
      : opts;
  };

  function toggleAllMalls() {
    const totalPairs = (mallList || []).reduce(
      (sum, mall) => sum + selectableTierOptions(mall.id).length,
      0
    );
    const selectedPairs = new Set(
      targetingEntries.map((row) => `${row.mallId}:${row.tierId}`)
    ).size;
    const allSelected = totalPairs > 0 && selectedPairs === totalPairs;

    if (allSelected) {
      const voucherCount = targetingEntries.reduce(
        (sum, row) => sum + (row.voucherIds?.length || 0),
        0
      );
      const doClear = () => {
        setTargetingEntries([]);
        setOpenMalls(new Set());
      };
      if (voucherCount > 0) {
        confirmVoucherClear(voucherCount, doClear);
      } else {
        doClear();
      }
      return;
    }

    (mallList || []).forEach((mall) => {
      const tierOptions = selectableTierOptions(mall.id);
      const mallEntries = targetingEntries.filter(
        (row) => String(row.mallId) === String(mall.id)
      );
      const selectedTierIds = new Set(
        mallEntries.map((row) => String(row.tierId))
      );
      tierOptions
        .filter((tier) => !selectedTierIds.has(String(tier.value)))
        .forEach((tier) => addTargetForTier(mall.id, tier.value));
    });
    setOpenMalls(new Set((mallList || []).map((mall) => mall.id)));
  }

  // Quick task 260720-ntx, extended Phase 9 SC-17: bulk toolbar applier for
  // BOTH Min Spend and Wins/Day in one pass. The two modes differ in whether
  // an EMPTY bulk box means anything:
  //
  //   'all'   — "make every row match these two boxes", verbatim. An empty box
  //             is a value: it CLEARS that field on every row. This is what an
  //             admin means when they blank a box and press Apply to all
  //             (developer, UAT round 6). The cost is that applying with one
  //             box blank also clears that column, so both boxes are read as
  //             the intended end state — which is why they sit side by side.
  //   'empty' — "fill in the gaps". Only rows missing a value are touched, and
  //             only from a box that HAS a value (isUnset — never bare
  //             truthiness, so an explicit 0 still counts as set). Filling a
  //             blank with a blank is meaningless, so an empty box is skipped
  //             here rather than treated as a clear. Existing per-tier
  //             overrides are never clobbered.
  function applyBulkDefaults(mode) {
    setTargetingEntries((prev) =>
      prev.map((row) => {
        const patch = {};

        if (mode === 'all') {
          patch.minSpendThreshold = bulkMinSpend;
          patch.winsPerDay = bulkWinsPerDay;
        } else if (mode === 'empty') {
          if (!isUnset(bulkMinSpend) && isUnset(row.minSpendThreshold)) {
            patch.minSpendThreshold = bulkMinSpend;
          }
          if (!isUnset(bulkWinsPerDay) && isUnset(row.winsPerDay)) {
            patch.winsPerDay = bulkWinsPerDay;
          }
        }

        return Object.keys(patch).length > 0 ? { ...row, ...patch } : row;
      })
    );
  }

  // Quick task 260720-ntx: bulk voucher toolbar. This is an explicit,
  // labeled bulk action — NOT a side effect of a mall/tier change — so it
  // does not go through confirmVoucherClear (D-03's confirm is scoped to
  // voucher loss as a SIDE EFFECT of removing/changing a target, not to a
  // deliberate "Clear vouchers" button click).
  function bulkVouchers(mode) {
    setTargetingEntries((prev) =>
      prev.map((row) => {
        if (mode === 'all') {
          return { ...row, voucherIds: (row.voucherPool || []).map((v) => v.id) };
        }
        if (mode === 'clear') {
          return { ...row, voucherIds: [] };
        }
        return row;
      })
    );
  }

  function setEntryMinSpend(addid, value) {
    handleTargetingEntryChange(addid, 'minSpendThreshold', value);
  }

  // Phase 9 SC-17 — per-pool daily win number, routed through the same
  // primitive setEntryMinSpend uses. Also covered by the bulk toolbar
  // (applyBulkDefaults, above) via its own bulkWinsPerDay box.
  function setEntryWinsPerDay(addid, value) {
    handleTargetingEntryChange(addid, 'winsPerDay', value);
  }

  function setEntryVouchers(addid, voucherIds) {
    handleTargetingEntryChange(addid, 'voucherIds', voucherIds);
  }

  // Phase 9 publish/alwaysWin toggles — thin wrappers over the existing
  // setChange primitive, mirroring every other scalar field's write path.
  function togglePublish() {
    // Past the end date the switch position is the admin's alone and never
    // reaches `values.publish` — the campaign's stored publish flag is not
    // this screen's to rewrite. Save is the gate (publishBlockedByEndDate).
    if (isPastEndDate) {
      setPublishAttempt((prev) => !prev);
      return;
    }
    setChange('publish', !values.publish);
  }

  function toggleAlwaysWin() {
    setChange('alwaysWin', !values.alwaysWin);
  }

  // Quick task 260720-ntx: memoized mall-grouped derivation the Targeting
  // accordion renders from. Pure derivation of targetingEntries + mallList +
  // mallTierOptions — never a second persisted structure (see
  // <design_decision> in the plan).
  const groupedMalls = useMemo(() => {
    const entryByKey = new Map();
    targetingEntries.forEach((row) => {
      entryByKey.set(`${row.mallId}:${row.tierId}`, row);
    });

    return (mallList || []).map((mall) => {
      const tierOptions = mallTierOptions[mall.id] || [];
      // Slab Upgrade: pills that are not a rule To-Tier render DISABLED with a
      // tooltip; when no complete rule exists yet, every pill is disabled with
      // a "define a rule first" hint.
      const noCompleteSlabRules =
        slabRewardTierIds !== null && slabRewardTierIds.size === 0;
      const tiers = tierOptions.map((tier) => {
        const entry = entryByKey.get(`${mall.id}:${tier.value}`);
        const slabDisabled =
          slabRewardTierIds !== null &&
          !slabRewardTierIds.has(String(tier.tierId));
        return {
          tierId: tier.value,
          conceptTierId: tier.tierId,
          name: tier.label,
          isSelected: Boolean(entry),
          slabDisabled,
          slabDisabledReason: slabDisabled
            ? noCompleteSlabRules
              ? 'Add an upgrade rule with a To-Tier first.'
              : "Only tiers that members upgrade into can be rewarded here. This tier isn't one of them in your Slab Upgrade rules."
            : undefined,
        };
      });
      // rows (the per-tier setup panel) show every selected entry, including an
      // orphan left by a trigger switch, so it stays visible/removable.
      const selectedTiers = tiers.filter((tier) => tier.isSelected);
      // The mall tri-state only reflects SELECTABLE (To-Tier) pills in slab
      // mode — disabled tiers never count toward checked/partial.
      const selectableTiers =
        slabRewardTierIds === null
          ? tiers
          : tiers.filter((tier) => !tier.slabDisabled);
      const selectedSelectable = selectableTiers.filter(
        (tier) => tier.isSelected
      );
      const status =
        selectedSelectable.length === 0
          ? 'unchecked'
          : selectableTiers.length > 0 &&
              selectedSelectable.length === selectableTiers.length
            ? 'checked'
            : 'partial';

      const rows = selectedTiers.map((tier) => {
        const entry = entryByKey.get(`${mall.id}:${tier.tierId}`);
        const minSpendThreshold = entry?.minSpendThreshold ?? '';
        const isOverridden = Boolean(
          minSpendThreshold !== '' &&
            minSpendThreshold !== null &&
            minSpendThreshold !== undefined &&
            String(minSpendThreshold) !== String(bulkMinSpend)
        );
        return {
          addid: entry?.addid,
          tierId: tier.tierId,
          tierName: tier.name,
          minSpendThreshold,
          winsPerDay: entry?.winsPerDay ?? '',
          voucherPool: entry?.voucherPool || [],
          voucherIds: entry?.voucherIds || [],
          assignedVouchers: entry?.assignedVouchers || [],
          isOverridden,
        };
      });

      const voucherTotal = rows.reduce(
        (sum, row) => sum + (row.voucherIds?.length || 0),
        0
      );
      const summary =
        selectedTiers.length > 0
          ? `${selectedTiers.length} target${selectedTiers.length > 1 ? 's' : ''} · ${voucherTotal} voucher${voucherTotal !== 1 ? 's' : ''}`
          : `${tiers.length} tier${tiers.length !== 1 ? 's' : ''}`;

      return {
        mallId: mall.id,
        mallName: mall.label,
        city: mall.city,
        tiers,
        status,
        isOpen: openMalls.has(mall.id),
        summary,
        rows,
      };
    });
  }, [
    mallList,
    mallTierOptions,
    targetingEntries,
    openMalls,
    bulkMinSpend,
    slabRewardTierIds,
  ]);

  // Phase 9 D-07/D-08 issuance-control guidance (non-blocking, mirrors the
  // server's assertIssuanceControlRule — 09-06-SUMMARY.md). Quick task
  // 260818-o5h: the endDate exemption is retired server-side — campaign
  // dates now gate visibility only, never pacing, so an end date no longer
  // covers a pool. alwaysWin is the only thing that covers every pool at
  // once; the per-pool winsPerDay requirement bites whenever alwaysWin is
  // off. isUnset (not truthiness) so an explicit winsPerDay of 0 is treated
  // as present, matching the server's own === null || === undefined check.
  const issuanceOffendingRows = useMemo(() => {
    if (values.alwaysWin === true) return [];
    return targetingEntries.filter((entry) => isUnset(entry.winsPerDay));
  }, [targetingEntries, values.alwaysWin]);
  const issuanceRuleUnmet = issuanceOffendingRows.length > 0;

  // D-06/D-08: maps a backend `errors[].property` string to the fieldErrors
  // key index.js renders against. Defensive/regex-based per RESEARCH Pitfall
  // 5 / Assumptions Log A1 — the exact nested-array property format
  // (`tierConfig.2.mallId` vs `tierConfig[2].mallId`) is unverified against
  // a live 400 response; both dot- and bracket-index separators are
  // tolerated. Unmatched/flat property names fall back to the raw property
  // string; those aren't rendered inline anywhere specific, so submitCampaign
  // toasts them (CR-02) instead of silently dropping them into fieldErrors.
  function mapSubmitErrorProperty(property) {
    if (!property) return 'general';

    const tierConfigMatch = /^tierConfig(?:\.|\[)(\d+)\]?/.exec(property);
    if (tierConfigMatch) {
      const idx = Number(tierConfigMatch[1]);
      return targetingEntries[idx]?.addid ?? 'general';
    }

    const voucherIdsMatch = /^voucherIds(?:\.|\[)(\d+)\]?/.exec(property);
    if (voucherIdsMatch) {
      const idx = Number(voucherIdsMatch[1]);
      // Same de-duplicated flatMap order buildCampaignFormData uses for the
      // voucherIds field, so the index lines up with the submitted payload.
      const flatVoucherIds = [
        ...new Set(targetingEntries.flatMap((e) => e.voucherIds || [])),
      ];
      const voucherId = flatVoucherIds[idx];
      const owningEntry = targetingEntries.find((e) =>
        (e.voucherIds || []).includes(voucherId)
      );
      return owningEntry
        ? `${owningEntry.addid}-voucher-${voucherId}`
        : 'general';
    }

    return property;
  }

  // CAMP-10 atomic submit. Client validate()/coverage are UX only — this
  // ALWAYS runs when invoked and the backend response is authoritative
  // (T-03-V4/V5): a 200 here is the only source of truth for success, and
  // both documented 400 shapes are handled without ever short-circuiting on
  // client-side state.
  //
  // Admin override: the post-start lock is gone, so EVERY edit now sends the
  // full structural payload (buildCampaignFormData) regardless of status —
  // identical to create. The backend accepts the full update at any lifecycle
  // stage (tierConfig replace + voucher reassignment run unconditionally;
  // validateVoucherPairs is told this campaign's own vouchers are legitimate).
  // The former buildPostStartFormData / status branching is removed.

  // campaignId presence toggles create vs. update (mirrors CreateVoucher's
  // storeId toggle). Admin override: both send the identical full structural
  // payload — there is no longer any status-based payload shaping.
  async function submitCampaign() {
    setPageLoading(true);
    try {
      const isEdit = Boolean(campaignId);
      let response;

      // Admin override: create and every edit send the same full structural
      // payload — the backend applies the whole update at any lifecycle stage.
      const formData = buildCampaignFormData(
        values,
        targetingEntries,
        slabUpgradePaths,
        voucherUpdates,
        originalVoucherIds
      );
      if (!isEdit) {
        response = await createCampaign(formData);
      } else {
        response = await updateCampaign(campaignId, formData);
      }

      if (response?.responseCode === 200) {
        Toast.success(
          isEdit ? Message.Campaign.UPDATED_SUCCESS : Message.Campaign.CREATED_SUCCESS
        );
        navigate(APP_CONSTANTS.GAMIFICATION_CAMPAIGNS);
        return;
      }

      const parsed = parseCampaignSubmitError(response);
      if (parsed.kind === 'field-errors') {
        const mapped = {};
        parsed.errors.forEach(({ property, message }) => {
          mapped[mapSubmitErrorProperty(property)] = message;
        });
        setFieldErrors(mapped);
        // CR-02: index.js only renders fieldErrors[entry.addid] and
        // fieldErrors[`${addid}-voucher-${id}`] — anything else (whole-array
        // errors like `tierConfig`, slabUpgradePaths[idx].*, top-level scalar
        // DTO errors, or the 'general' fallback) would otherwise clear
        // pageLoading with zero visible feedback. Surface those via toast.
        const unrendered = Object.keys(mapped).filter(
          (k) =>
            !targetingEntries.some(
              (e) => k === e.addid || k.startsWith(`${e.addid}-voucher-`)
            )
        );
        if (unrendered.length > 0) {
          Toast.error(unrendered.map((k) => mapped[k]).join('; '));
        }
      } else {
        Toast.error(parsed.message);
      }
    } catch (err) {
      Toast.error(Message?.Error?.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }

  function handleCreateCampaign(e) {
    e.preventDefault();
    const validationErrors = validate(values, campaignValidationRules);

    // Trigger-conditional required-row check (CAMP-03): the declarative
    // validation engine cannot express array-length rules, so this lives
    // here rather than in campaignValidationRules. At least one COMPLETE
    // (fromTierId and toTierId both set) Slab Upgrade path is required
    // when triggerType === 'SLAB_UPGRADE'.
    if (values.triggerType === 'SLAB_UPGRADE') {
      const hasCompletePath = slabUpgradePaths.some(
        (row) => row.fromTierId && row.toTierId
      );
      if (!hasCompletePath) {
        validationErrors.slabUpgradePaths =
          'At least one complete upgrade path (From-tier and To-tier) is required';
      }

      // Same-tier backstop (amends CAMP-03, quick task 260715-exi): a row
      // with both tiers set and equal is by definition NOT incomplete, so
      // the hasCompletePath check above can never fire for that same row —
      // the two conditions are mutually exclusive per row. When a same-tier
      // row exists, its distinct message takes precedence over the
      // "complete path" message on the shared validationErrors.slabUpgradePaths
      // key (assigned unconditionally, after hasCompletePath, when found).
      const hasSameTierRow = slabUpgradePaths.some(
        (row) =>
          row.fromTierId &&
          row.toTierId &&
          String(row.fromTierId) === String(row.toTierId)
      );
      if (hasSameTierRow) {
        validationErrors.slabUpgradePaths =
          'A From-tier and To-tier in the same upgrade path must be different tiers';
      }
    }

    // Trigger-conditional required-field checks (item 4,
    // .planning/TRIGGER-EDGE-CASES.md): same rationale as the SLAB_UPGRADE
    // block above — the declarative campaignValidationRules engine cannot
    // express "required only when triggerType === X", so these live here.
    // Defensive missing-check (undefined/null/blank-after-trim), never naive
    // truthiness — an explicit '0' for birthdayWindowDays is a legitimate
    // value ("exact day only") and must NOT be treated as missing.
    if (values.triggerType === 'SINGLE_DAY_SPEND') {
      const raw = values.singleDaySpendMin;
      const isMissing =
        raw === undefined || raw === null || String(raw).trim() === '';
      if (isMissing) {
        validationErrors.singleDaySpendMin =
          'Minimum Amount is required for the Single-Day Spend trigger';
      }
    }

    if (values.triggerType === 'BIRTHDAY') {
      const raw = values.birthdayWindowDays;
      const isMissing =
        raw === undefined || raw === null || String(raw).trim() === '';
      if (isMissing) {
        validationErrors.birthdayWindowDays =
          'Days Before Birthday is required for the Birthday trigger';
      } else if (Number(raw) > 30) {
        // Item 6 (.planning/TRIGGER-EDGE-CASES.md, locked 2026-07-16):
        // inclusive 30-day ceiling — an admin typo (e.g. 200 instead of 20)
        // must not silently create a near-permanent birthday trigger.
        // Matches the backend's @Max(30) decorator (inclusive: 30 valid,
        // 31 rejected). Only checked when a value is present so a blank
        // field produces only the required error above, never both.
        validationErrors.birthdayWindowDays =
          'Days Before Birthday must not exceed 30';
      } else if (!Number.isInteger(Number(raw))) {
        // Mirrors the backend's @IsInt — "2.5 days before" is not a thing,
        // and without this it came back as a raw 400.
        validationErrors.birthdayWindowDays =
          'Days Before Birthday must be a whole number';
      }
    }

    // Trigger-conditional required-window check (quick task 260819-e14):
    // same rationale as the SINGLE_DAY_SPEND/BIRTHDAY blocks above — for
    // DATE_BASED the campaign window IS the trigger's only condition. The
    // backend's GameEligibilityService DATE_BASED case returns eligible
    // unconditionally and relies entirely on the window gate, so a blank
    // start or end date here means a campaign that can never fire. Scoped
    // to this trigger only: every other trigger carries its own condition
    // and keeps the window optional (campaignValidationRules.startDate is
    // deliberately `[]`, Phase 9 SC-2). The shared rules object is left
    // alone because validate() has no conditional/when support. Each date
    // is checked independently (no else) so both errors can surface on one
    // submit, and only the missing branch assigns an error so a
    // present-but-reversed pair keeps the endDateNotBeforeStartDate message
    // validate() already put on endDate.
    if (values.triggerType === 'DATE_BASED') {
      const rawStart = values.startDate;
      const isStartMissing =
        rawStart === undefined ||
        rawStart === null ||
        String(rawStart).trim() === '';
      if (isStartMissing) {
        validationErrors.startDate =
          'Campaign Start Date is required for the Date-Based trigger';
      }

      const rawEnd = values.endDate;
      const isEndMissing =
        rawEnd === undefined || rawEnd === null || String(rawEnd).trim() === '';
      if (isEndMissing) {
        validationErrors.endDate =
          'Campaign End Date is required for the Date-Based trigger';
      }
    }

    // Bill-window requirement, mirroring the backend's
    // assertBillDatesRequiredWhenBillReading. A campaign "reads bills" when
    // the trigger is SINGLE_DAY_SPEND, or when ANY targeting row carries a
    // minimum spend — in both cases the engine needs to know WHICH invoice
    // dates count, and the server 400s without the pair. Previously the FE
    // had no check at all, so the admin got a raw backend error with no
    // field attached. Both dates are checked independently so a half-filled
    // window reports only the missing half.
    const readsBills =
      values.triggerType === 'SINGLE_DAY_SPEND' ||
      targetingEntries.some((entry) => !isUnset(entry.minSpendThreshold));
    if (readsBills) {
      const billReason =
        values.triggerType === 'SINGLE_DAY_SPEND'
          ? 'the trigger is Single-Day Spend'
          : 'a minimum spend is set on a target';
      if (isUnset(values.billStartDate)) {
        validationErrors.billStartDate = `Bill Start Date is required because ${billReason}`;
      }
      if (isUnset(values.billEndDate)) {
        validationErrors.billEndDate = `Bill End Date is required because ${billReason}`;
      }
    }

    // CR-02: nothing else client-side blocks submitting with zero targeting
    // entries — the empty state (index.js) is a legal, submittable UI
    // state. Guard here so the backend's "tierConfig must contain at least
    // 1 elements" 400 is never actually reachable; it also gives the admin
    // an immediate, rendered error instead of a silent no-op submit.
    if (targetingEntries.length === 0) {
      validationErrors.targeting =
        'At least one targeting entry (Mall → Tier) is required';
    }

    // Bug found via manual admin testing: a targeting row with a Mall
    // selected but no Tier (or vice versa) previously submitted straight
    // through to the backend and crashed with a raw 500 there — Mall AND
    // Tier are both mandatory per entry (SOW v1.4 §4.2 "Mall selection" as
    // a campaign-wide parameter, §4.4 Flow 2 "target tier as a mandatory
    // field at the time of entry itself"). Mirrors the SLAB_UPGRADE
    // completeness check above, but per-entry rather than "at least one
    // complete row" — every row must be complete, not just one of them.
    // Reuses the existing fieldErrors[entry.addid] inline slot (the same
    // one backend field-errors render into, see index.js) so this needs no
    // new UI wiring.
    const incompleteEntries = targetingEntries.filter(
      (entry) => isUnset(entry.mallId) || isUnset(entry.tierId)
    );
    if (incompleteEntries.length > 0) {
      const incompleteFieldErrors = {};
      incompleteEntries.forEach((entry) => {
        incompleteFieldErrors[entry.addid] = isUnset(entry.mallId)
          ? 'Mall is required for this target'
          : 'Tier is required for this target';
      });
      setFieldErrors((prev) => ({ ...prev, ...incompleteFieldErrors }));
      validationErrors.targeting =
        'Every targeting entry needs both a Mall and a Tier selected';
    }

    // Voucher coverage is MANDATORY only when the campaign has no points
    // budget (quick task 260819-e3y-rewrite-admin-campaign-save-validation):
    // a campaign now rewards vouchers, points, or both — a voucher-less pool
    // is legitimate once points are configured, since it rewards points
    // instead. Every COMPLETE targeting entry (Mall + Tier both set) must
    // have at least one voucher assigned ONLY when points are not set, else
    // a member could play but never win. Only complete rows are checked — an
    // incomplete row already surfaces its own Mall/Tier fieldError above, and
    // stacking a second error on the same row's addid slot would clobber it.
    // Reuses getUncoveredTargets (voucherIds.length === 0) filtered to
    // complete rows, and the same fieldErrors[entry.addid] inline slot.
    if (!hasPointsConfigured(values)) {
      const uncoveredComplete = getUncoveredTargets(targetingEntries).filter(
        (entry) => !isUnset(entry.mallId) && !isUnset(entry.tierId)
      );
      if (uncoveredComplete.length > 0) {
        const uncoveredFieldErrors = {};
        uncoveredComplete.forEach((entry) => {
          uncoveredFieldErrors[entry.addid] =
            'Add a voucher, or set a points budget';
        });
        setFieldErrors((prev) => ({ ...prev, ...uncoveredFieldErrors }));
        validationErrors.targeting =
          'Each target needs a voucher, or set a points budget for the campaign';
      }
    }

    // Per-target Wins/Day: whole number, 0 or greater — mirrors the backend's
    // @IsInt + @Min(0) on TierConfigDto.winsPerDay, which had no client-side
    // equivalent at all, so a negative or decimal value went straight through
    // to a raw 400. Optional: a blank cell means "no daily cap" and is left
    // alone. Guarded on !validationErrors.targeting so a more fundamental
    // targeting error (zero rows / incomplete row / no voucher) shows first
    // rather than being clobbered in the shared fieldErrors[addid] slot.
    if (!validationErrors.targeting) {
      const badWinsPerDay = targetingEntries.filter((entry) => {
        if (isUnset(entry.winsPerDay)) return false;
        const num = Number(entry.winsPerDay);
        return Number.isNaN(num) || num < 0 || !Number.isInteger(num);
      });
      if (badWinsPerDay.length > 0) {
        const winsFieldErrors = {};
        badWinsPerDay.forEach((entry) => {
          winsFieldErrors[entry.addid] =
            'Wins/Day must be a whole number, 0 or greater';
        });
        setFieldErrors((prev) => ({ ...prev, ...winsFieldErrors }));
        validationErrors.targeting =
          'Wins/Day must be a whole number, 0 or greater';
      }
    }

    // Slab Upgrade coverage (global): every rule To-Tier must be targeted in
    // AT LEAST ONE selected mall — a member only ever lands in a rule's
    // To-Tier, so an untargeted To-Tier means that upgrade path rewards no one.
    // Also backstops that no NON-destination tier is targeted (the pills
    // disable that, so this only catches an orphan left by a trigger switch).
    // Compared on the CONCEPT id (rule To-Tiers, and each entry's mall-tier
    // concept via conceptTierIdOfEntry) — never the per-mall Default_Tiers id.
    // Guarded so a more fundamental targeting error (zero/incomplete/voucher)
    // shows first.
    if (values.triggerType === 'SLAB_UPGRADE' && !validationErrors.targeting) {
      const rewardSet = new Set();
      slabUpgradePaths.forEach((row) => {
        if (row.fromTierId && row.toTierId) rewardSet.add(String(row.toTierId));
      });
      const coveredConcepts = new Set();
      targetingEntries.forEach((entry) => {
        const concept = conceptTierIdOfEntry(entry);
        if (concept !== undefined) coveredConcepts.add(String(concept));
      });
      const conceptLabel = (cid) =>
        tierList.find((t) => String(t.value) === String(cid))?.label ||
        `Tier ${cid}`;

      const uncovered = [...rewardSet].filter((c) => !coveredConcepts.has(c));
      const invalid = [...coveredConcepts].filter((c) => !rewardSet.has(c));

      if (uncovered.length > 0) {
        validationErrors.targeting = `Every upgrade destination must be targeted in at least one mall. Missing: ${uncovered
          .map(conceptLabel)
          .join(', ')}.`;
      } else if (invalid.length > 0) {
        validationErrors.targeting = `Only upgrade-destination tiers can be targeted for a Slab Upgrade campaign. Remove: ${invalid
          .map(conceptLabel)
          .join(', ')}.`;
      }
    }

    // Quick task 260716-dqa: submit-time backstop for the duplicate
    // (mallId, tierId) 500 — the DB has a unique constraint on
    // (campaignId, mallId, tierId), so sending two targeting entries with
    // the same pair raises a raw TypeORM/Postgres error the controller
    // can't map to a clean 400. This mirrors the same-tier slabUpgradePaths
    // backstop above (260715-exi).
    // Quick task 260716-e0w: the duplicate-pair message is delivered as a
    // Toast (matching this class of feedback elsewhere in the app), not the
    // shared `validationErrors.targeting` inline key — it no longer competes
    // with the zero-entries inline message above.
    const hasDuplicatePairs = hasDuplicateTargetingPairs(targetingEntries);
    if (hasDuplicatePairs) {
      Toast.error(Message.Campaign.TARGETING_DUPLICATE_PAIR);
    }

    // An ended campaign cannot be saved with Publish switched on: the backend
    // derives ENDED from the end date and it outranks the publish flag
    // (deriveCampaignStatus), so the save would appear to work and change
    // nothing an admin could see. Delivered as a Toast, like the duplicate-
    // pair backstop above — an inline message here has nowhere to sit but
    // inside the toggle card, which then grows and shunts Save down the page.
    //
    // Keyed on publishBlockedByEndDate (the admin's switch position), never on
    // values.publish — that stays TRUE for every already-published ended
    // campaign, so keying on it would block voucher-count corrections and
    // every other legitimate edit rather than just the republish attempt.
    if (publishBlockedByEndDate) {
      Toast.error(Message.Campaign.PUBLISH_AFTER_END_DATE);
    }

    setErrors(validationErrors);

    if (
      Object.keys(validationErrors).length === 0 &&
      !hasDuplicatePairs &&
      !publishBlockedByEndDate
    ) {
      setFieldErrors({});
      submitCampaign();
    }
  }

  return {
    values,
    error,
    fieldErrors,
    tierList,
    mallList,
    pageLoading,
    slabUpgradePaths,
    targetingEntries,
    // Quick task 260720-ntx: mall-grouped Targeting accordion state/handlers.
    mallTierOptions,
    groupedMalls,
    openMalls,
    toggleMallOpen,
    bulkMinSpend,
    setBulkMinSpend,
    bulkWinsPerDay,
    setBulkWinsPerDay,
    toggleMallTier,
    toggleMall,
    toggleAllMalls,
    applyBulkDefaults,
    bulkVouchers,
    setEntryMinSpend,
    setEntryWinsPerDay,
    setEntryVouchers,
    togglePublish,
    toggleAlwaysWin,
    isPastEndDate,
    publishDisplayValue,
    publishBlockedByEndDate,
    issuanceRuleUnmet,
    issuanceOffendingRows,
    // CAMP-07 edit-mode
    detail,
    campaignId,
    isLocked,
    canReleaseVouchers,
    isVoucherRemovable,
    handleRemoveAssignedVoucher,
    voucherUpdates,
    voucherUpdateErrors,
    handleVoucherUpdateChange,
    handleChange,
    addUpgradePath,
    removeUpgradePath,
    handleUpgradePathChange,
    uploadMediaHandler,
    handleCreateCampaign,
    navigate,
  };
};

export default useCreateCampaign;
